/**
 * RoomsPage: Raum-Verwaltung.
 *
 * Filter: nach Standort.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiCallError, type Room, type Location } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { Drawer } from "../components/Drawer";
import { Badge } from "../components/Badge";
import { ListToolbar } from "../components/ListToolbar";
import { ActiveToggle } from "../components/ActiveToggle";
import { useAuth } from "../lib/auth";
import { t } from "../locales/de";
import { matchesListSearch } from "../lib/listFiltering";

type FormState = {
  locationId: string;
  name: string;
  capacity: string;
  notes: string;
  active: boolean;
};

function emptyForm(locations: Location[]): FormState {
  return {
    locationId: locations[0]?.id?.toString() ?? "",
    name: "",
    capacity: "10",
    notes: "",
    active: true,
  };
}

export function RoomsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filterLocationId, setFilterLocationId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Room | null>(null);
  const [form, setForm] = useState<FormState>({ locationId: "", name: "", capacity: "10", notes: "", active: true });
  const [error, setError] = useState<string | null>(null);

  const canWrite = user?.role === "admin" || user?.role === "planer";
  const canDelete = user?.role === "admin";

  const locQ = useQuery({ queryKey: ["locations"], queryFn: () => api.listLocations() });
  const locations = locQ.data?.locations ?? [];

  const roomQ = useQuery({
    queryKey: ["rooms", filterLocationId],
    queryFn: () => api.listRooms(filterLocationId ? Number(filterLocationId) : undefined),
  });
  const rooms = roomQ.data?.rooms ?? [];

  const createMut = useMutation({
    mutationFn: (body: Partial<Room>) => api.createRoom(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rooms"] });
      setDrawerOpen(false);
    },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Room> }) => api.updateRoom(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rooms"] });
      setDrawerOpen(false);
    },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteRoom(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rooms"] }),
    onError: (e) => alert(e instanceof ApiCallError ? e.message : "Löschen fehlgeschlagen"),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm(locations));
    setError(null);
    setDrawerOpen(true);
  }
  function openEdit(room: Room) {
    setEditing(room);
    setForm({
      locationId: room.locationId.toString(),
      name: room.name,
      capacity: room.capacity.toString(),
      notes: room.notes ?? "",
      active: room.isActive,
    });
    setError(null);
    setDrawerOpen(true);
  }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      locationId: Number(form.locationId),
      name: form.name.trim(),
      capacity: Number(form.capacity),
      notes: editing ? form.notes.trim() : form.notes.trim() || undefined,
      isActive: form.active,
    };
    if (!body.name) return setError("Name ist erforderlich");
    if (!Number.isFinite(body.capacity) || body.capacity < 1)
      return setError("Kapazität muss eine positive Zahl sein");
    if (editing) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate(body);
  }

  const locName = (id: number) => locations.find((l) => l.id === id)?.name ?? `Standort ${id}`;
  const visibleRooms = rooms.filter((room) => matchesListSearch(search, [room.name, locName(room.locationId), room.notes]));
  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.rooms}</h1>
        {canWrite && <Button onClick={openCreate}>+ Neuer Raum</Button>}
      </div>

      <ListToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Raum, Standort oder Notiz suchen…" resultCount={visibleRooms.length} hasActiveFilters={Boolean(search || filterLocationId)} onReset={() => { setSearch(""); setFilterLocationId(""); }}>
        <select
          aria-label="Nach Standort filtern"
          value={filterLocationId}
          onChange={(e) => setFilterLocationId(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
        >
          <option value="">Alle Standorte</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </ListToolbar>

      <div className="rounded-lg border border-slate-200 bg-white">
        {roomQ.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade…</div>
        ) : visibleRooms.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine Räume für die aktuelle Suche oder Filterauswahl.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Standort</th>
                <th className="px-4 py-3">Kapazität</th>
                <th className="px-4 py-3">Notizen</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {visibleRooms.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{locName(r.locationId)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.capacity}</td>
                  <td className="px-4 py-3 text-slate-600">{r.notes ?? "—"}</td>
                  <td className="px-4 py-3">
                    {r.isActive ? <Badge variant="success">Aktiv</Badge> : <Badge variant="neutral">Inaktiv</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canWrite && (
                        <ActiveToggle
                          checked={r.isActive}
                          disabled={updateMut.isPending}
                          label={`Raum ${r.name}`}
                          onChange={() => updateMut.mutate({ id: r.id, body: { isActive: !r.isActive } })}
                        />
                      )}
                      {canWrite && <Button variant="secondary" onClick={() => openEdit(r)}>Bearbeiten</Button>}
                      {canDelete && (
                        <Button variant="danger" onClick={() => {
                          if (confirm(`Raum "${r.name}" wirklich löschen?`)) deleteMut.mutate(r.id);
                        }}>Löschen</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? "Raum bearbeiten" : "Neuer Raum"}
      >
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
          )}
          <div>
            <Label htmlFor="locationId">Standort *</Label>
            <select
              id="locationId"
              value={form.locationId}
              onChange={(e) => setForm({ ...form, locationId: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              required
            >
              <option value="">— bitte wählen —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="capacity">Kapazität *</Label>
            <Input id="capacity" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Input id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
            <Label htmlFor="active" className="!mb-0">Aktiv</Label>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setDrawerOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Speichere…" : editing ? "Speichern" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
